import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import adminApiService from '../../services/adminApiService';
import { toast } from 'react-toastify';

const AdminTokenRechargeHistory = () => {
  const { agentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [rechargeHistory, setRechargeHistory] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (agentId) {
      fetchAgentRechargeHistory();
    }
  }, [agentId]);

  const fetchAgentRechargeHistory = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getTokenRechargeHistory(agentId);
      if (response.success) {
        setRechargeHistory(response.tokenRechargeHistory || []);
        setAgentInfo({
          agentId: response.agentId,
          agentEmail: response.username || response.agentEmail,
          totalRecharges: response.totalRecords || (response.tokenRechargeHistory ? response.tokenRechargeHistory.length : 0)
        });
      } else {
        toast.error(response.message || 'Failed to fetch agent token ledger history');
      }
    } catch (error) {
      console.error('Error fetching agent token history:', error);
      toast.error('Failed to fetch agent token history');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeBadge = (type) => {
    switch (type) {
      case 'DEFAULT_ALLOCATION':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800"><i className="fas fa-user-check mr-1"></i> Default Allocation</span>;
      case 'BOOKING_REWARD':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800"><i className="fas fa-gift mr-1"></i> Booking Reward</span>;
      case 'EXTRA_TOKENS_GRANTED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800"><i className="fas fa-coins mr-1"></i> Extra Tokens Granted</span>;
      case 'USAGE_DEDUCTION':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800"><i className="fas fa-minus-circle mr-1"></i> Usage Deduction</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-800">{type || 'TRANSACTION'}</span>;
    }
  };

  const filteredRecharges = rechargeHistory.filter(recharge => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      (recharge.transactionId && recharge.transactionId.toLowerCase().includes(term)) ||
      (recharge.tokenId && recharge.tokenId.toLowerCase().includes(term)) ||
      (recharge.transactionType && recharge.transactionType.toLowerCase().includes(term)) ||
      (recharge.reason && recharge.reason.toLowerCase().includes(term)) ||
      (recharge.source && recharge.source.toLowerCase().includes(term)) ||
      (recharge.performedByName && recharge.performedByName.toLowerCase().includes(term))
    );
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agent Token Audit Ledger</h1>
            {agentInfo && (
              <p className="text-gray-600 mt-1">
                Agent ID: <span className="font-mono font-bold text-blue-700">{agentInfo.agentId}</span> {agentInfo.agentEmail ? `(${agentInfo.agentEmail})` : ''} - {agentInfo.totalRecharges} total ledger records
              </p>
            )}
          </div>
          <Link 
            to="/admin/dashboard/agent-token-recharge" 
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium border text-sm flex items-center transition"
          >
            ← Back to Token Accounts
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search Audit Ledger</label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            placeholder="Search by Transaction ID, Type, Reason, Source..."
          />
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Token Ledger Records ({filteredRecharges.length} records)
          </h2>
        </div>
        
        {filteredRecharges && filteredRecharges.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Txn ID</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Date & Time</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-center">Tokens</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-center">Prev Balance</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase text-center">New Balance</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase">Performed By</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm">
                {filteredRecharges.map((recharge, index) => (
                  <tr key={recharge.transactionId || recharge.tokenId || index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-900">{recharge.transactionId || recharge.tokenId || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{recharge.transactionDate || recharge.rechargedDate || 'N/A'}</td>
                    <td className="px-4 py-3">{getTransactionTypeBadge(recharge.transactionType)}</td>
                    <td className="px-4 py-3 text-center font-bold text-green-700">
                      {recharge.amount != null ? (recharge.amount > 0 ? `+${recharge.amount}` : recharge.amount) : (recharge.noOfTokensRecharge || '+20')}
                    </td>
                    <td className="px-4 py-3 text-center font-medium text-gray-600">{recharge.previousBalance ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-center font-bold text-blue-900">{recharge.balanceAfter ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-800 max-w-xs truncate" title={recharge.reason}>{recharge.reason || 'N/A'}</td>
                    <td className="px-4 py-3 text-xs font-semibold text-gray-600">{recharge.source || 'SYSTEM'}</td>
                    <td className="px-4 py-3 text-xs text-gray-700">{recharge.performedByName || recharge.performedBy || 'System'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-center text-gray-500">
            No token ledger transactions found for this agent.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTokenRechargeHistory;
